import {
  Check,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Pencil,
  Tag as TagIcon,
  X,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.js';
import { cn } from '../../../lib/utils.js';
import { useAuth } from '../../../lib/auth-context.js';
import { TeamsDropdown, TEAM_ALL } from '../../common/components/TeamsDropdown.js';
import { useDayLogs } from '../../planning/hooks.js';
import { useScenariosList } from '../../scenario/hooks.js';
import { FOLDER_ALL, FOLDER_UNCATEGORIZED } from '../../scenario/components/ScenarioPageHeader.js';
import {
  applyScenarioFilter,
  decodeScenarioFilter,
  encodeScenarioFilter,
  ScenarioFilterDrawer,
  type ScenarioFilterValue,
} from '../../scenario/components/ScenarioFilterDrawer.js';
import { ScenarioToolbar } from '../../scenario/components/ScenarioToolbar.js';
import type { ScenarioRow as ScenarioRowData, ScenarioStatusFilter } from '../../scenario/types.js';
import { asScenarioRow, isActive } from '../../scenario/types.js';
import { formatDuration } from '../../scenario/utils.js';
import { ScenarioDetailModal } from '../../scenario/components/ScenarioDetailModal.js';
import { ScenarioTagSelector } from '../../scenario/components/ScenarioTagSelector.js';
import { presetToDateRange } from '../../kpi/hooks.js';
import {
  PresentationDateRangeDropdown,
  type PresentationDateRangePreset,
} from '../../presentation/components/PresentationDateRangeDropdown.js';
import { ValuePagesToggle } from '../components/ValuePagesToggle.js';

/**
 * Value · Metrics — per-scenario rollup table. Mirrors the legacy
 * `wf-audit/src/slices/value/pages/metrics.tsx` view: each row is a
 * scenario; the columns are period-summed value + execution stats.
 *
 *   ┌───┬──────────┬───────┬───────┬─────────────┬─────────────┬──────────┬──────────┐
 *   │ # │ Scenario │ Hours │ Value │ Succ Exec % │ Operations% │ Data Tx  │ Duration │
 *   └───┴──────────┴───────┴───────┴─────────────┴─────────────┴──────────┴──────────┘
 *
 * ROI formula (matches legacy):
 *   • `hours = hoursSaved × successCount`
 *   • `value = hoursSaved × hourlyRate × successCount`
 *
 *   `hoursSaved` and `hourlyRate` are per-scenario operator inputs
 *   stored on `fusion_scenario.hours_saved` / `fusion_scenario.hourly_rate`
 *   (NUMERIC(12,2) → string on the wire to preserve cents). They're
 *   editable from the page itself: clicking the pencil in the `Value`
 *   column header reveals two extra columns (`Hourly Rate (Est.)` +
 *   `Hours Per Execution`) with per-row number inputs; ✓ saves the
 *   pending edits via `PUT /v1/scenarios/{fusionId}`, ✕ discards.
 *
 *   `successCount`, `operations`, `transfer`, `duration` come from
 *   `useDayLogs()` summed in-memory across the selected date range.
 *   No server-side rollup endpoint yet — the day-log payload is small
 *   enough to fold client-side.
 *
 * Successful Executions % + Operations % are computed against the
 * currently-visible rows' totals so filtering rescales the bars.
 *
 * Pre-2026-05-26 this page rendered a metric-definitions glossary;
 * operators flagged it as wrong vs the legacy. PR #143 introduced
 * the per-scenario table; PR #144 added sorting. This rewrite swaps
 * the Hours/Value source from the KPI rollup to the operator-input
 * ROI pair (matches the legacy edit mode the operator screenshotted).
 */
export function ValueMetricsPage(): JSX.Element {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ScenarioStatusFilter>('all');
  const [dateRange, setDateRange] = useState<PresentationDateRangePreset>('last-30-days');
  // Default sort: highest hours saved first. Operators scan the page
  // looking for the scenarios pulling the most automation weight;
  // descending hours puts those at the top by default.
  const [sortField, setSortField] = useState<SortField>('hours');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Edit mode: when true, two extra editable columns appear and
  // per-row inputs become editable. Pending edits live in `edits` —
  // keyed by fusionId; only the changed rows hit the network on
  // save.
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState<Map<string, { hourlyRate: string; hoursSaved: string }>>(
    () => new Map(),
  );
  const [saving, setSaving] = useState(false);
  const { api, session } = useAuth();
  const qc = useQueryClient();

  // Detail modal is URL-driven so the operator can deep-link / share
  // a scenario URL — same `?scenarioId=` contract as the Scenarios
  // list page.
  const [params, setParams] = useSearchParams();
  const openScenarioId = params.get('scenarioId');
  const openScenario = useCallback(
    (id: string): void => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('scenarioId', id);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const closeModal = useCallback((): void => {
    const next = new URLSearchParams(params);
    next.delete('scenarioId');
    setParams(next);
  }, [params, setParams]);

  // Folder + Team filters — URL-backed so they round-trip the same
  // way as the Scenarios list (`?folder=` / `?team=`). Sentinels
  // `FOLDER_ALL` and `TEAM_ALL` reuse the canonical constants.
  const folder = params.get('folder') ?? FOLDER_ALL;
  const team = params.get('team') ?? TEAM_ALL;
  const patchParam = useCallback(
    (key: string, v: string, defaultValue: string): void => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (v === defaultValue) next.delete(key);
          else next.set(key, v);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const setFolder = useCallback((v: string) => patchParam('folder', v, FOLDER_ALL), [patchParam]);
  const setTeam = useCallback((v: string) => patchParam('team', v, TEAM_ALL), [patchParam]);
  // Drawer filter (modules range, ops range, modifiedBy, apps, tags)
  // is URL-pinned via the same `?filter=` base64url-JSON contract as
  // the Scenarios list so deep-links round-trip.
  const filterEncoded = params.get('filter');
  const filter = useMemo<ScenarioFilterValue>(
    () => decodeScenarioFilter(filterEncoded),
    [filterEncoded],
  );
  const setFilter = useCallback(
    (next: ScenarioFilterValue) => {
      patchParam('filter', encodeScenarioFilter(next), '');
    },
    [patchParam],
  );

  const handleSortChange = (field: SortField): void => {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'name' ? 'asc' : 'desc');
    }
  };

  const { from, to } = useMemo(() => presetToDateRange(dateRange), [dateRange]);
  const { data: dayLogRaw = [], isLoading: dayLogsLoading } = useDayLogs();
  const {
    data: scenarioRows = [],
    isLoading: scenariosLoading,
    isFetching: scenariosFetching,
  } = useScenariosList();

  // Refresh = invalidate both the scenarios list and the day-logs
  // queries so the rollup picks up new executions + any operator
  // edits to hours_saved / hourly_rate. Toast nudges the operator
  // so they know something is happening.
  const licenseKey = session?.licenseId ?? 'anon';
  const refresh = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['scenarios', 'list', licenseKey] });
    void qc.invalidateQueries({ queryKey: ['logs', 'day', licenseKey] });
    toast.success('Refreshing value metrics…');
  }, [qc, licenseKey]);

  const scenarios = useMemo<ReadonlyArray<ScenarioRowData>>(
    () => (scenarioRows ?? []).map(asScenarioRow),
    [scenarioRows],
  );

  // Day-logs roll-up: sum per fusionScenarioId across the selected
  // date range. `useDayLogs` doesn't take params yet; the payload is
  // small enough to filter in-memory.
  const executionRollup = useMemo(() => {
    const out = new Map<
      string,
      { successCount: number; operations: number; transfer: number; duration: number }
    >();
    for (const raw of dayLogRaw) {
      const row = raw as {
        fusionScenarioId?: unknown;
        date?: unknown;
        successCount?: unknown;
        operations?: unknown;
        transfer?: unknown;
        duration?: unknown;
      };
      const sid = typeof row.fusionScenarioId === 'string' ? row.fusionScenarioId : null;
      if (!sid) continue;
      const date = typeof row.date === 'string' ? row.date : null;
      if (date && (date < from || date > to)) continue;
      const totals = out.get(sid) ?? {
        successCount: 0,
        operations: 0,
        transfer: 0,
        duration: 0,
      };
      totals.successCount += toFiniteNumber(row.successCount);
      totals.operations += toFiniteNumber(row.operations);
      totals.transfer += toFiniteNumber(row.transfer);
      totals.duration += toFiniteNumber(row.duration);
      out.set(sid, totals);
    }
    return out;
  }, [dayLogRaw, from, to]);

  // Build the decorated row set: scenario + pending edit overrides +
  // execution rollup + computed hours/value. Filters apply first, the
  // sort runs last. `edits` overrides the persisted `hourlyRate` /
  // `hoursSaved` so the table reads the in-progress values back while
  // the operator is mid-edit.
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    // Drawer filter (modules / ops range, modifiedBy, apps, tags,
    // last-run, etc.) goes first — it's the most expensive narrower.
    return applyScenarioFilter(scenarios, filter)
      .filter((s) => {
        if (status === 'active' && !isActive(s)) return false;
        if (status === 'inactive' && isActive(s)) return false;
        // Folder: `__all__` keeps everything; `__uncategorized__`
        // matches rows with no folder; otherwise exact-name match.
        // Same semantics as the Scenarios list.
        if (folder === FOLDER_UNCATEGORIZED) {
          if (s.folderName && s.folderName.length > 0) return false;
        } else if (folder !== FOLDER_ALL) {
          if (s.folderName !== folder) return false;
        }
        if (team !== TEAM_ALL && s.teamFusionId !== team) return false;
        if (needle) {
          const hay = (s.name ?? '').toLowerCase();
          if (!hay.includes(needle)) return false;
        }
        return true;
      })
      .map((s) => {
        const exec = executionRollup.get(s.fusionId) ?? {
          successCount: 0,
          operations: 0,
          transfer: 0,
          duration: 0,
        };
        const pending = edits.get(s.fusionId);
        const hourlyRateStr = pending?.hourlyRate ?? s.hourlyRate ?? '';
        const hoursSavedStr = pending?.hoursSaved ?? s.hoursSaved ?? '';
        const hourlyRate = parseNumeric(hourlyRateStr);
        const hoursSaved = parseNumeric(hoursSavedStr);
        const hours = hoursSaved * exec.successCount;
        const value = hours * hourlyRate;
        return {
          row: s,
          hourlyRateStr,
          hoursSavedStr,
          hourlyRate,
          hoursSaved,
          hours,
          value,
          ...exec,
        };
      })
      .sort((a, b) => {
        const direction = sortDir === 'asc' ? 1 : -1;
        if (sortField === 'name') {
          return (a.row.name ?? '').localeCompare(b.row.name ?? '') * direction;
        }
        const va = a[sortField];
        const vb = b[sortField];
        if (va === vb) return 0;
        return (va < vb ? -1 : 1) * direction;
      });
  }, [scenarios, search, status, folder, team, filter, executionRollup, edits, sortField, sortDir]);

  // Totals across the filtered set — drive both the percentage bars
  // and the footer's grand-total row.
  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, r) => ({
        hours: acc.hours + r.hours,
        value: acc.value + r.value,
        successCount: acc.successCount + r.successCount,
        operations: acc.operations + r.operations,
        transfer: acc.transfer + r.transfer,
        duration: acc.duration + r.duration,
      }),
      {
        hours: 0,
        value: 0,
        successCount: 0,
        operations: 0,
        transfer: 0,
        duration: 0,
      },
    );
  }, [filtered]);

  const counts = useMemo(() => {
    let active = 0;
    let inactive = 0;
    for (const s of scenarios) {
      if (isActive(s)) active += 1;
      else inactive += 1;
    }
    return { all: scenarios.length, active, inactive };
  }, [scenarios]);

  // Unique folder names referenced by the license's scenarios, used
  // to populate the dropdown next to the "Value:" title. Same shape
  // as the Scenarios list's folderOptions.
  const folderOptions = useMemo<ReadonlyArray<string>>(() => {
    const names = new Set<string>();
    for (const s of scenarios) {
      if (s.folderName && s.folderName.length > 0) names.add(s.folderName);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [scenarios]);

  const folderLabel =
    folder === FOLDER_ALL
      ? 'All scenarios'
      : folder === FOLDER_UNCATEGORIZED
        ? 'Uncategorized'
        : folder;

  const isLoading = dayLogsLoading || scenariosLoading;

  // Edit-mode handlers. Persisted values aren't mutated until the
  // operator clicks ✓; ✕ discards the pending map entirely and the
  // table reverts to the engine-served values on the next render.
  const startEditing = useCallback(() => {
    setEditing(true);
    setEdits(new Map());
  }, []);
  const cancelEditing = useCallback(() => {
    setEditing(false);
    setEdits(new Map());
  }, []);
  const stageEdit = useCallback(
    (fusionId: string, field: 'hourlyRate' | 'hoursSaved', value: string) => {
      setEdits((prev) => {
        const next = new Map(prev);
        const current = next.get(fusionId) ?? {
          hourlyRate: '',
          hoursSaved: '',
        };
        // Seed the *unchanged* field with the persisted value so we
        // don't accidentally wipe it on save when only one field was
        // touched.
        const scenario = scenarios.find((s) => s.fusionId === fusionId);
        const seeded = {
          hourlyRate: current.hourlyRate || scenario?.hourlyRate || '',
          hoursSaved: current.hoursSaved || scenario?.hoursSaved || '',
        };
        next.set(fusionId, { ...seeded, [field]: value });
        return next;
      });
    },
    [scenarios],
  );
  const commitEdits = useCallback(async (): Promise<void> => {
    if (edits.size === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const failures: string[] = [];
    // Sequential PUTs to avoid hammering the engine when the operator
    // edits many rows at once. Each row is a tiny round-trip (~50 ms)
    // so the wall-clock budget is bounded by the operator's edit
    // count; in practice they touch 1-5 rows before saving.
    for (const [fusionId, patch] of edits.entries()) {
      try {
        await api.put(`/v1/scenarios/${encodeURIComponent(fusionId)}`, {
          hourlyRate: normaliseNumericInput(patch.hourlyRate),
          hoursSaved: normaliseNumericInput(patch.hoursSaved),
        });
      } catch (err) {
        failures.push(fusionId);
        const msg = err instanceof Error ? err.message : String(err);
        toast.error(`Save failed for ${fusionId}: ${msg}`);
      }
    }
    setSaving(false);
    if (failures.length === 0) {
      toast.success(`Saved ${edits.size} scenario${edits.size === 1 ? '' : 's'}`);
      setEditing(false);
      setEdits(new Map());
      // Invalidate the scenarios-list cache so the table re-reads the
      // freshly-persisted `hourlyRate` / `hoursSaved` from the engine
      // on the next render. The Dashboard + Scenarios list pages
      // share this key, so they refresh in lockstep.
      void qc.invalidateQueries({ queryKey: ['scenarios', 'list', session?.licenseId ?? 'anon'] });
    } else {
      // Keep the still-failed entries staged so the operator can
      // retry just those.
      setEdits((prev) => {
        const next = new Map<string, { hourlyRate: string; hoursSaved: string }>();
        for (const id of failures) {
          const v = prev.get(id);
          if (v) next.set(id, v);
        }
        return next;
      });
    }
  }, [api, edits]);

  return (
    <div className="relative z-10 flex h-[calc(100vh-3.5rem)] flex-col overflow-hidden">
      <header className="flex flex-wrap items-center gap-3 px-6 pb-2 pt-6">
        <h1 className="text-2xl font-light text-foreground">Value:</h1>
        <span className="text-foreground/30">|</span>
        {/* Folder dropdown — same trigger style + behaviour as the
            Scenarios list page. `__all__` shows everything;
            `__uncategorized__` matches rows without a folder. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-base text-foreground/80 transition-colors hover:border-foreground/15 hover:bg-foreground/5 hover:text-foreground"
            >
              <span>{folderLabel}</span>
              <ChevronDown className="h-4 w-4 text-foreground/50" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-[60vh] min-w-[220px] overflow-y-auto">
            <DropdownMenuLabel>Folder</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={folder === FOLDER_ALL}
              onCheckedChange={() => setFolder(FOLDER_ALL)}
            >
              All scenarios
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={folder === FOLDER_UNCATEGORIZED}
              onCheckedChange={() => setFolder(FOLDER_UNCATEGORIZED)}
            >
              Uncategorized
            </DropdownMenuCheckboxItem>
            {folderOptions.length > 0 ? <DropdownMenuSeparator /> : null}
            {folderOptions.map((name) => (
              <DropdownMenuCheckboxItem
                key={name}
                checked={folder === name}
                onCheckedChange={() => setFolder(name)}
              >
                {name}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="flex-1" />
        <ValuePagesToggle />
        <div className="flex-1" />
        <PresentationDateRangeDropdown value={dateRange} onChange={setDateRange} />
        {/* Teams dropdown replaces the old Export CSV button on the
            right — operator-confirmed legacy parity. */}
        <TeamsDropdown team={team} onTeamChange={setTeam} />
      </header>

      {/* Search + filter drawer + centered status pills + refresh —
          single shared component with the Scenarios list so the
          layout (3-col grid, true center) and behaviour stay in
          lock-step. */}
      <ScenarioToolbar
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={setStatus}
        counts={counts}
        filterDrawer={<ScenarioFilterDrawer value={filter} onChange={setFilter} rows={scenarios} />}
        onRefresh={refresh}
        isRefreshing={scenariosFetching}
      />

      <div className="min-h-0 flex-1 overflow-auto px-6 pb-4">
        <table className="w-full border-separate border-spacing-0 text-sm">
          <thead className="sticky top-0 z-10 bg-[color:var(--gradient-end)]">
            <tr className="text-xs uppercase tracking-wide text-foreground/55">
              <th className="border-b border-foreground/10 px-2 py-2 text-left">#</th>
              <SortHead
                field="name"
                label="Scenario"
                align="left"
                sortField={sortField}
                sortDir={sortDir}
                onChange={handleSortChange}
              />
              <SortHead
                field="hours"
                label="Hours Saved"
                align="right"
                tint="violet"
                sortField={sortField}
                sortDir={sortDir}
                onChange={handleSortChange}
              />
              <th className="border-b border-foreground/10 bg-amber-500/[0.08] px-2 py-2 text-right">
                <span className="inline-flex items-center gap-1.5">
                  <SortLabel
                    field="value"
                    label="Value"
                    sortField={sortField}
                    sortDir={sortDir}
                    onChange={handleSortChange}
                  />
                  {editing ? (
                    <span className="inline-flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() => void commitEdits()}
                        disabled={saving}
                        className="rounded p-1 text-emerald-600 hover:bg-emerald-500/15 hover:text-emerald-700 disabled:opacity-40"
                        aria-label="Save edits"
                        title={saving ? 'Saving…' : 'Save edits'}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={cancelEditing}
                        disabled={saving}
                        className="rounded p-1 text-rose-600 hover:bg-rose-500/15 hover:text-rose-700 disabled:opacity-40"
                        aria-label="Cancel edits"
                        title="Cancel edits"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={startEditing}
                      className="rounded border border-foreground/15 p-1 text-foreground/60 hover:border-foreground/40 hover:text-foreground"
                      aria-label="Edit hourly rate and hours per execution"
                      title="Edit hourly rate + hours per execution"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                </span>
              </th>
              {editing ? (
                <>
                  <th className="border-b border-foreground/10 px-2 py-2 text-right">
                    Hourly Rate (Est.)
                  </th>
                  <th className="border-b border-foreground/10 px-2 py-2 text-right">
                    Hours Per Execution
                  </th>
                </>
              ) : null}
              <SortHead
                field="successCount"
                label="Successful Executions"
                align="right"
                tint="emerald"
                sortField={sortField}
                sortDir={sortDir}
                onChange={handleSortChange}
              />
              <SortHead
                field="operations"
                label="Operations"
                align="right"
                tint="blue"
                sortField={sortField}
                sortDir={sortDir}
                onChange={handleSortChange}
              />
              <SortHead
                field="transfer"
                label="Data Transfer"
                align="right"
                sortField={sortField}
                sortDir={sortDir}
                onChange={handleSortChange}
              />
              <SortHead
                field="duration"
                label="Duration"
                align="right"
                sortField={sortField}
                sortDir={sortDir}
                onChange={handleSortChange}
              />
            </tr>
          </thead>
          <tbody className="text-foreground/85">
            {isLoading && filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={editing ? 10 : 8}
                  className="px-2 py-8 text-center text-sm text-foreground/45"
                >
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={editing ? 10 : 8}
                  className="px-2 py-8 text-center text-sm text-foreground/45"
                >
                  No scenarios match the current filters.
                </td>
              </tr>
            ) : (
              filtered.map((r, i) => (
                <tr
                  key={r.row.fusionId}
                  className="border-b border-foreground/5 hover:bg-foreground/[0.03]"
                >
                  <td className="px-2 py-1.5 text-foreground/55">{i + 1}</td>
                  <td className="px-2 py-1.5 text-foreground">
                    <div className="flex items-center gap-1.5">
                      {/* Bare tag-icon trigger that opens the same
                          `ScenarioTagSelector` popover the detail
                          modal uses — legacy parity (operator: "in
                          legacy system we had tag icon for each
                          scenario row that opens popup with tags
                          list"). */}
                      <ScenarioTagSelector
                        fusionId={r.row.fusionId}
                        currentTagIds={(r.row.tags ?? []).map((t) => t.id)}
                        trigger={
                          <button
                            type="button"
                            aria-label={`Edit tags for ${r.row.name}`}
                            title="Edit tags"
                            className="rounded p-1 text-foreground/45 transition-colors hover:bg-foreground/10 hover:text-foreground"
                          >
                            <TagIcon className="h-3.5 w-3.5" />
                          </button>
                        }
                      />
                      {/* Clicking the name opens the same detail
                          modal the Scenarios list uses — keeps the
                          two pages' interactions consistent. */}
                      <button
                        type="button"
                        onClick={() => openScenario(r.row.fusionId)}
                        className="text-left text-foreground hover:underline focus:underline focus:outline-none"
                      >
                        {r.row.name}
                      </button>
                    </div>
                  </td>
                  <BarCell
                    value={r.hours}
                    max={totals.hours}
                    formatted={r.hours > 0 ? formatNumber(r.hours) : '0'}
                    tint="violet"
                  />
                  <BarCell
                    value={r.value}
                    max={totals.value}
                    formatted={r.value > 0 ? `$${formatNumber(r.value)}` : '$0'}
                    tint="amber"
                  />
                  {editing ? (
                    <>
                      <td className="px-2 py-1 text-right">
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={r.hourlyRateStr}
                          onChange={(e) => stageEdit(r.row.fusionId, 'hourlyRate', e.target.value)}
                          disabled={saving}
                          className="w-24 rounded border border-foreground/15 bg-foreground/5 px-2 py-1 text-right text-sm tabular-nums text-foreground focus:border-foreground/40 focus:outline-none disabled:opacity-50"
                          aria-label={`Hourly rate for ${r.row.name}`}
                        />
                      </td>
                      <td className="px-2 py-1 text-right">
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={r.hoursSavedStr}
                          onChange={(e) => stageEdit(r.row.fusionId, 'hoursSaved', e.target.value)}
                          disabled={saving}
                          className="w-24 rounded border border-foreground/15 bg-foreground/5 px-2 py-1 text-right text-sm tabular-nums text-foreground focus:border-foreground/40 focus:outline-none disabled:opacity-50"
                          aria-label={`Hours per execution for ${r.row.name}`}
                        />
                      </td>
                    </>
                  ) : null}
                  <PctCell count={r.successCount} total={totals.successCount} tint="emerald" />
                  <PctCell count={r.operations} total={totals.operations} tint="blue" />
                  <td className="px-2 py-1.5 text-right tabular-nums">{formatBytes(r.transfer)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {r.duration > 0 ? formatDuration(r.duration) : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {filtered.length > 0 ? (
            <tfoot className="sticky bottom-0 bg-[color:var(--gradient-end)] text-sm font-semibold text-foreground">
              <tr>
                <td className="border-t border-foreground/15 px-2 py-2"></td>
                <td className="border-t border-foreground/15 px-2 py-2">Total</td>
                <td className="border-t border-foreground/15 bg-violet-500/[0.08] px-2 py-2 text-right tabular-nums">
                  {formatNumber(totals.hours)}
                </td>
                <td className="border-t border-foreground/15 bg-amber-500/[0.08] px-2 py-2 text-right tabular-nums">
                  ${formatNumber(totals.value)}
                </td>
                {editing ? (
                  <>
                    <td className="border-t border-foreground/15 px-2 py-2"></td>
                    <td className="border-t border-foreground/15 px-2 py-2"></td>
                  </>
                ) : null}
                <td className="border-t border-foreground/15 bg-emerald-500/[0.08] px-2 py-2 text-right tabular-nums">
                  {formatNumber(totals.successCount)}
                </td>
                <td className="border-t border-foreground/15 bg-blue-500/[0.08] px-2 py-2 text-right tabular-nums">
                  {formatNumber(totals.operations)}
                </td>
                <td className="border-t border-foreground/15 px-2 py-2 text-right tabular-nums">
                  {formatBytes(totals.transfer)}
                </td>
                <td className="border-t border-foreground/15 px-2 py-2 text-right tabular-nums">
                  {totals.duration > 0 ? formatDuration(totals.duration) : '—'}
                </td>
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      <ScenarioDetailModal
        fusionId={openScenarioId}
        listingRow={
          openScenarioId ? scenarios.find((s) => s.fusionId === openScenarioId) : undefined
        }
        onClose={closeModal}
        // Value Metrics is a Business/Operator surface. Showing the
        // same level of scenario detail as the Scenarios list under
        // the developer view here would be inconsistent — keep this
        // modal in 'business' mode so it matches the page's audience.
        role="business"
      />
    </div>
  );
}

type SortField =
  'name' | 'hours' | 'value' | 'successCount' | 'operations' | 'transfer' | 'duration';

/**
 * Sort label + chevron only — no surrounding `<th>` cell. Used by the
 * `Value` column whose header packs the sort-by widget alongside the
 * edit pencil / save / cancel buttons.
 */
function SortLabel({
  field,
  label,
  sortField,
  sortDir,
  onChange,
}: {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDir: 'asc' | 'desc';
  onChange: (f: SortField) => void;
}): JSX.Element {
  const active = sortField === field;
  const Glyph = !active ? ChevronsUpDown : sortDir === 'asc' ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      onClick={() => onChange(field)}
      className={cn(
        'inline-flex items-center gap-1.5 rounded px-1 py-0.5 transition-colors hover:text-foreground',
        active ? 'text-foreground' : '',
      )}
    >
      <span>{label}</span>
      <Glyph className="h-3 w-3" />
    </button>
  );
}

/**
 * Header cell with click-to-sort + direction glyph. Pattern lifted
 * from `scenario/components/ScenarioListTable.tsx`.
 */
function SortHead({
  field,
  label,
  align,
  tint,
  sortField,
  sortDir,
  onChange,
}: {
  field: SortField;
  label: string;
  align: 'left' | 'right';
  tint?: 'violet' | 'amber' | 'emerald' | 'blue';
  sortField: SortField;
  sortDir: 'asc' | 'desc';
  onChange: (f: SortField) => void;
}): JSX.Element {
  const active = sortField === field;
  const Glyph = !active ? ChevronsUpDown : sortDir === 'asc' ? ChevronUp : ChevronDown;
  const tintBg: Record<NonNullable<typeof tint>, string> = {
    violet: 'bg-violet-500/[0.08]',
    amber: 'bg-amber-500/[0.08]',
    emerald: 'bg-emerald-500/[0.08]',
    blue: 'bg-blue-500/[0.08]',
  };
  return (
    <th
      className={cn(
        'border-b border-foreground/10 px-2 py-2',
        align === 'right' ? 'text-right' : 'text-left',
        tint ? tintBg[tint] : '',
      )}
    >
      <button
        type="button"
        onClick={() => onChange(field)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded px-1 py-0.5 transition-colors hover:text-foreground',
          align === 'right' ? 'flex-row-reverse' : '',
          active ? 'text-foreground' : '',
        )}
      >
        <span>{label}</span>
        <Glyph className="h-3 w-3" />
      </button>
    </th>
  );
}

function BarCell({
  value,
  max,
  formatted,
  tint,
}: {
  value: number;
  max: number;
  formatted: string;
  tint: 'violet' | 'amber';
}): JSX.Element {
  const pct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  const bg: Record<typeof tint, string> = {
    violet: 'bg-violet-500/30',
    amber: 'bg-amber-500/30',
  };
  return (
    <td className="relative px-2 py-1.5 text-right tabular-nums">
      {value > 0 ? (
        <span
          className={cn('absolute inset-y-1 left-2 rounded-sm', bg[tint])}
          style={{ width: `${pct}%` }}
        />
      ) : null}
      <span className="relative">{formatted}</span>
    </td>
  );
}

function PctCell({
  count,
  total,
  tint,
}: {
  count: number;
  total: number;
  tint: 'emerald' | 'blue';
}): JSX.Element {
  const pct = total > 0 ? (count / total) * 100 : 0;
  const bg: Record<typeof tint, string> = {
    emerald: 'bg-emerald-500/30',
    blue: 'bg-blue-500/30',
  };
  return (
    <td className="relative px-2 py-1.5 text-right tabular-nums">
      {count > 0 ? (
        <span
          className={cn('absolute inset-y-1 left-2 rounded-sm', bg[tint])}
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      ) : null}
      <span className="relative">
        {formatNumber(count)}
        {total > 0 ? ` (${pct.toFixed(1)}%)` : ''}
      </span>
    </td>
  );
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(n);
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—';
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${bytes} B`;
}

function toFiniteNumber(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/**
 * Parse a NUMERIC(12,2)-as-string into a JS number for the ROI
 * computation. Trims whitespace, treats empty / non-numeric as 0
 * (operators see "0" in the cell, not NaN).
 */
function parseNumeric(raw: string | null | undefined): number {
  if (!raw) return 0;
  const trimmed = raw.trim();
  if (!trimmed) return 0;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Format the operator's edit-mode input back into the
 * `NUMERIC(12,2)` wire shape the engine's `scenario-update` schema
 * expects (matches `^\d+(\.\d{1,2})?$`). Empty input → `'0'` so the
 * field round-trips cleanly even when the operator cleared it.
 */
function normaliseNumericInput(raw: string): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return '0';
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return '0';
  // Cap at 2 decimal places (engine schema validates this).
  return n.toFixed(2).replace(/\.00$/, '');
}
